import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card'
import { TrendingUp, DollarSign, ShoppingCart, Users } from 'lucide-react'

export function AnalyticsPage() {
  const { currentStore } = useStore()

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', currentStore?.id],
    queryFn: async () => {
      const [products, orders, customers, categories] = await Promise.all([
        supabase.from('products').select('price, stock, status').eq('store_id', currentStore?.id),
        supabase.from('orders').select('total, status').eq('store_id', currentStore?.id),
        supabase.from('customers').select('total_spent').eq('store_id', currentStore?.id),
        supabase.from('categories').select('id', { count: 'exact' }).eq('store_id', currentStore?.id),
      ])

      const totalRevenue = orders.data?.reduce((sum, o) => sum + (o.total || 0), 0) || 0
      const totalOrders = orders.data?.length || 0
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0
      
      const inventoryValue = products.data?.reduce((sum, p) => sum + (p.price * p.stock), 0) || 0
      const totalProducts = products.data?.length || 0
      const activeProducts = products.data?.filter(p => p.status === 'active').length || 0
      const lowStock = products.data?.filter(p => p.stock < 10).length || 0

      const totalCustomers = customers.data?.length || 0
      const avgCustomerValue = totalCustomers > 0 && customers.data
        ? customers.data.reduce((sum, c) => sum + c.total_spent, 0) / totalCustomers 
        : 0

      return {
        revenue: totalRevenue,
        orders: totalOrders,
        avgOrderValue,
        customers: totalCustomers,
        products: { total: totalProducts, active: activeProducts, lowStock },
        inventory: inventoryValue,
        avgCustomerValue,
        categories: categories.count || 0,
      }
    },
    enabled: !!currentStore?.id,
  })

  if (isLoading) return <BaseLayout><LoadingSpinner /></BaseLayout>

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader title="Analytics & Reports" description="Track your store performance" />

        <div className="grid gap-6 md:grid-cols-4 mb-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${analytics?.revenue.toFixed(2)}</div>
              <div className="flex items-center gap-1 text-sm mt-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600">+12.5%</span>
                <span className="text-muted-foreground">vs last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.orders}</div>
              <div className="flex items-center gap-1 text-sm mt-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600">+8.3%</span>
                <span className="text-muted-foreground">vs last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Avg Order Value</CardTitle>
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${analytics?.avgOrderValue.toFixed(2)}</div>
              <div className="flex items-center gap-1 text-sm mt-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600">+4.2%</span>
                <span className="text-muted-foreground">vs last month</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Customers</CardTitle>
                <Users className="h-5 w-5 text-orange-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics?.customers}</div>
              <div className="flex items-center gap-1 text-sm mt-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600">+15.7%</span>
                <span className="text-muted-foreground">vs last month</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Inventory Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Products</span>
                  <span className="font-semibold">{analytics?.products.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Active Products</span>
                  <span className="font-semibold text-green-600">{analytics?.products.active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Low Stock Items</span>
                  <span className="font-semibold text-red-600">{analytics?.products.lowStock}</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-sm text-muted-foreground">Inventory Value</span>
                  <span className="font-bold">${analytics?.inventory.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Customer Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Customers</span>
                  <span className="font-semibold">{analytics?.customers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Avg Customer Value</span>
                  <span className="font-semibold">${analytics?.avgCustomerValue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Repeat Rate</span>
                  <span className="font-semibold">32%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Store Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Categories</span>
                  <span className="font-semibold">{analytics?.categories}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Products</span>
                  <span className="font-semibold">{analytics?.products.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Orders</span>
                  <span className="font-semibold">{analytics?.orders}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </BaseLayout>
  )
}
